import { useInfiniteQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import { Button } from "@/components/ui/button.tsx";
import { cn } from "@/lib/utils.ts";

import { TASK_DIALOG_CONTENT_CLASS, TaskList } from "./TaskList.tsx";
import { type TaskFilters, taskQueries } from "./useTasks.ts";
import { CanvasPivotPanel } from "@/components/layout/SplitPanel/CanvasPivotPanel.tsx";
import { PanelToolbar } from "@/components/layout/SplitPanel/PanelToolbar.tsx";
import { ReactNode } from "react";

const OPEN_ONLY: TaskFilters = { status: "open" };
const EVERY_TASK: TaskFilters = {};

/** One Open/All pill; `isActive` drives the pressed fill. */
const FilterButton = ({
  label,
  isActive,
  onClick,
  statsigName,
}: {
  label: string;
  isActive: boolean;
  onClick: () => void;
  statsigName: string;
}) => (
  <Button
    variant="ghost"
    size="sm"
    className={cn("h-7 px-2.5 text-xs", isActive ? "bg-accent" : "text-muted-foreground")}
    aria-pressed={isActive}
    onClick={onClick}
    data-statsig-name={statsigName}
  >
    {label}
  </Button>
);

/**
 * Every task on the account — what the assistant is working on, and what came of
 * what it already did. The per-thread record lives in {@link ThreadTasksDialog}
 * instead.
 *
 * Shell-independent so the same rows serve two surfaces: a modal opened from the
 * threads header, and the Tasks canvas view. Only the frame around them differs.
 *
 * No `enabled` flag: the modal renders this inside `DialogContent`, which Radix
 * unmounts while closed, so a closed dialog holds no observer to disable. That
 * matters because the app-level task socket listener invalidates the cache and
 * React Query refetches only while something is observing.
 */
export const AccountTasksBody = ({
  filters: controlledFilters,
  onSelectThread,
}: {
  /**
   * Supplied by a host that shows the status choice itself (the canvas view
   * spells it out on its action row). When set, the inline pills below stand
   * down — two live copies of one choice is one too many.
   */
  filters?: TaskFilters;
  /** Passed to each row; see `TaskList`'s own `onSelectThread`. */
  onSelectThread?: (threadId: number) => void;
}) => {
  // Open first: the question this answers at a glance is "what's outstanding",
  // not "what has ever been recorded".
  const [ownFilters, setFilters] = useState<TaskFilters>(OPEN_ONLY);
  const filters = controlledFilters ?? ownFilters;
  const isShowingOpen = filters.status === "open";

  const { data, fetchNextPage, hasNextPage, isError, isFetchingNextPage, isLoading, refetch } =
    useInfiniteQuery(taskQueries.accountTasks(filters));

  // Rebuilt on every render otherwise — including each socket-driven
  // invalidation — which defeats bailout in the row list below.
  const tasks = useMemo(() => data?.pages.flatMap((page) => page.tasks) ?? [], [data]);
  const handleRetry = useCallback(() => void refetch(), [refetch]);

  return (
    <>
      {!controlledFilters && (
        <div className="flex shrink-0 items-center gap-1">
          <FilterButton
            label="Open"
            isActive={isShowingOpen}
            onClick={() => setFilters(OPEN_ONLY)}
            statsigName="account-tasks-filter-open"
          />
          <FilterButton
            label="All"
            isActive={!isShowingOpen}
            onClick={() => setFilters(EVERY_TASK)}
            statsigName="account-tasks-filter-all"
          />
        </div>
      )}
      {/* `min-h-0` is load-bearing: a flex child's default `min-height:auto`
            refuses to shrink below its content, so without it this never
            scrolls and the cap just clips. */}
      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
        <TaskList
          tasks={tasks}
          isLoading={isLoading}
          isError={isError}
          onRetry={handleRetry}
          showThread
          onSelectThread={onSelectThread}
          emptyMessage={isShowingOpen ? "Nothing open right now." : "No tasks on this account yet."}
        />
        {hasNextPage && (
          <Button
            variant="outline"
            size="sm"
            className="shrink-0"
            onClick={() => void fetchNextPage()}
            disabled={isFetchingNextPage}
            data-statsig-name="account-tasks-load-more"
          >
            {isFetchingNextPage ? "Loading…" : "Load more"}
          </Button>
        )}
      </div>
    </>
  );
};

/**
 * The modal framing of {@link AccountTasksBody}, opened from the threads panel
 * header. The Tasks canvas view renders the same body without this shell.
 */
export const AccountTasksDialog = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className={TASK_DIALOG_CONTENT_CLASS}>
      <DialogHeader className="shrink-0">
        <DialogTitle>Tasks</DialogTitle>
        <DialogDescription className="sr-only">
          Tasks Okthx is tracking across this account
        </DialogDescription>
      </DialogHeader>
      <AccountTasksBody />
    </DialogContent>
  </Dialog>
);

const STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "open", label: "Open" },
] as const;

/**
 * Tasks on the canvas: everything the assistant is working on, with the status
 * choice spelled out on the action row above the list.
 *
 * No search field, deliberately: `/api/a/tasks` filters by status only, and the
 * list is paginated, so a client-side box would search the pages already loaded
 * and quietly miss the rest — worse than no search at all.
 */
export const TasksCanvasView = ({
  accountSwitcher,
  leading,
  onSelectThread,
}: {
  accountSwitcher?: ReactNode;
  leading?: ReactNode;
  /**
   * Where a task's thread goes. Without it the row falls back to the thread's
   * own page, which leaves this canvas — and the sidebar with it — for the
   * standalone two-column layout.
   */
  onSelectThread?: (threadId: number) => void;
}) => {
  // Open first, same as the modal: a page that opens on the whole record buries
  // the outstanding work under everything already settled. `defaultValue` below
  // says so too, so this is the resting state rather than a filter the reader
  // is held to have applied — picking All is the thing that counts as narrowing
  // here, because it is the choice that differs from what the screen opened on.
  const [status, setStatus] = useState<"open" | "all">("open");
  const filters = status === "open" ? OPEN_ONLY : EVERY_TASK;

  return (
    <CanvasPivotPanel
      title="Tasks"
      leading={leading}
      accountSwitcher={accountSwitcher}
      toolbar={
        <PanelToolbar
          description="What Okthx is working on, and what came of what it already did."
          controls={{
            filters: [
              {
                label: "Status",
                value: status,
                onChange: (value) => setStatus(value === "open" ? "open" : "all"),
                options: STATUS_OPTIONS,
                // What this screen opens on, which is what a resting value
                // means here — not the widest of the two. Picking All is the
                // choice that differs from what the page opened on, so that is
                // what counts as narrowing, and arriving is not a filter the
                // reader applied.
                defaultValue: "open",
                display: "segmented",
                statsigName: "tasks-canvas-status",
              },
            ],
          }}
          subject="tasks"
          statsigName="tasks-canvas-filter"
        />
      }
    >
      <AccountTasksBody filters={filters} onSelectThread={onSelectThread} />
    </CanvasPivotPanel>
  );
};
